import BackgroundProgressiveImg from "app/pages/.shared/BackgroundProgressiveImg";
import Paragraphs from "app/pages/.shared/Paragraphs";
import PropTypes from "prop-types";
import { createRef, memo, useCallback, useContext } from "react";
import AppGlobalsContext from "app/AppGlobalsContext";
import { RESOLUTION } from "app/pages/.shared/responsive/responsiveReducer";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import "./SmartDPHome.scss";
import LoginContainer from "app/pages/Auth/Signin/LoginContainer";
import { FormattedMessage } from "react-intl";
import SignupContainer from "app/pages/Auth/Signup/SignupContainer";
import ChangePasswordContainer from "app/pages/Auth/ChangePassword/ChangePasswordContainer";
import ResetPasswordContainer from "app/pages/Auth/ResetPassword/ResetPasswordContainer";
import flatten from "lodash/flatten";
import { scrollToElement } from "app/utils/scroller";
import ProductsCarouselBlock from "app/pages/.shared/DynamicBlocks/blocks/ProductsCarousel/ProductsCarouselBlock";

const IMG_SIZES = {
	medium: { width: 750, height: 320 },
	small: { width: 750, height: 320 },
	large: { width: 2880 },
	xlarge: { width: 2880 },
};

const SmartDPHome = ({ home = {}, homeBlocks = [] }) => {
	const { resolution } = useContext(AppGlobalsContext);
	const authRef = createRef();
	const isMobile = resolution === RESOLUTION.SMALL || resolution === RESOLUTION.MEDIUM;
	const images = home.images;
	const texts = home.texts;
	const location = useLocation();

	const ppCarouselBlock = flatten(
		homeBlocks.map(block => (block._type === "multiBlocks" ? block.children : block))
	).find(b => b._type === "PPCarousel");

	const AuthForm = (
		<Routes>
			<Route
				path="/login"
				element={
					<LoginContainer
						signinButtonLabel={<FormattedMessage id="home.header.button.signin.now" />}
						headline={false}
						showTopFacebook={true}
					/>
				}
			/>
			<Route
				path="/signup"
				element={<SignupContainer headline={false} showTopFacebook={true} />}
			/>
			<Route
				path="/changepassword"
				element={<ChangePasswordContainer location={location} />}
			/>
			<Route path="/resetpassword" element={<ResetPasswordContainer />} />
			<Route path="/*" element={<Navigate to="./signup" />} />
		</Routes>
	);

	const handleClick = useCallback(() => {
		scrollToElement({ element: authRef.current, disableSmoothScroll: false });
	}, [authRef]);

	return (
		<div className="sdp-home">
			<div className="sdp-home__hero">
				<BackgroundProgressiveImg
					sizes={IMG_SIZES}
					resolution={resolution}
					backgroundPosition={"right"}
					thumbnail={
						isMobile ? images?.imageMobileThumbnail : images?.imageDesktopThumbnail
					}
					src={isMobile ? images?.imageMobile : images?.imageDesktop}
				/>
				<div className="sdp-home__hero-content">
					<div className="sdp-home__marketing">
						<div className="sdp-home__marketing-container">
							<Routes>
								<Route
									path="/login"
									element={
										<>
											<h2>
												<Paragraphs
													paragraphs={texts?.signinTitle}
													enableHTMLFormatting={true}
												/>
											</h2>
											<div className="sdp-home__subtitle">
												<Paragraphs
													paragraphs={texts?.signinDescription}
													enableHTMLFormatting={true}
												/>
											</div>
										</>
									}
								/>

								<Route
									path="/signup"
									element={
										<>
											<h2>
												<Paragraphs
													paragraphs={texts?.signupTitle}
													enableHTMLFormatting={true}
												/>
											</h2>
											<div className="sdp-home__subtitle">
												<Paragraphs
													paragraphs={texts?.signupDescription}
													enableHTMLFormatting={true}
												/>
											</div>
										</>
									}
								/>

								<Route
									path="/changepassword"
									element={
										<h2>
											<FormattedMessage id="auth.reset.password.marketing.text" />
										</h2>
									}
								/>

								<Route
									path="/resetpassword"
									element={
										<h2>
											<FormattedMessage id="auth.reset.password.marketing.text" />
										</h2>
									}
								/>
							</Routes>
						</div>
					</div>
					{!isMobile && (
						<div className="sdp-home__form" ref={authRef}>
							{AuthForm}
						</div>
					)}
				</div>
			</div>
			{isMobile && (
				<div className="sdp-home__form" ref={authRef}>
					{AuthForm}
				</div>
			)}
			<div className="sdp-home__blocks">
				{ppCarouselBlock && (
					<div className="sdp-home__blocks-item">
						<ProductsCarouselBlock
							introduction={ppCarouselBlock.intro}
							title={ppCarouselBlock.title}
							cta={ppCarouselBlock.cta}
							onItemClick={handleClick}
						/>
					</div>
				)}
			</div>
		</div>
	);
};

SmartDPHome.propTypes = {
	home: PropTypes.object,
	homeBlocks: PropTypes.array,
};

export default memo(SmartDPHome);
